import { useLayoutEffect } from 'react';

// The static shell predates Helmet. Remove its competing tags only while
// a page with its own Helmet metadata is mounted, before Helmet commits.
export function useMarketingHeadOwnership(enabled = true) {
  useLayoutEffect(() => {
    if (!enabled) return;
    document.head.querySelectorAll('meta[name="description"]:not([data-rh]), meta[property="og:title"]:not([data-rh]), meta[property="og:description"]:not([data-rh]), link[rel="canonical"]:not([data-rh])')
      .forEach((tag) => tag.remove());
  }, [enabled]);
}

// Public marketing surfaces (Sandy 2026-07-27).
//
// Two places key off this, and they must not drift apart:
//   - App.js decides app chrome. Marketing routes ALWAYS get the top
//     NavigationBar and never the student side rail, whoever is signed in.
//     Before this, the rail was chosen purely by role, so a signed-in student
//     landing on "/" got the app rail instead of the marketing header.
//   - NavigationBar.jsx decides which links and which CTA to render.
//
// Deliberately an exact-match list, not a prefix match: "/students" is
// marketing, "/student-dashboard" is the app.
// "/" is the TEACHER pitch (Sandy 2026-11-03; it was the student pitch from
// 2026-08-12) and "/for-schools" the schools pitch; "/schools" stays the
// partner-school registration page.
// The landing redos went live 2026-10-28 (Sandy): /teachers (connect story),
// /for-schools (schools landing), / and /students (student landing),
// /testimonials and /contact. Their old "-preview" paths redirect (App.js).
// /for-schools stays in the 'school' audience; its CTA is overridden by
// ctaJump below.
export const MARKETING_PATHS = ['/', '/schools', '/for-schools', '/teachers', '/students', '/testimonials', '/contact'];

// The one prefix exception: the District Scores explorer is a public pitch
// surface with real sub-routes (/district-scores/<district>/<school>), so the
// whole subtree wears marketing chrome. "/district-scoresfoo" must NOT match,
// hence the exact-or-slash test rather than startsWith alone.
const MARKETING_PREFIXES = ['/district-scores'];

export const isMarketingRoute = (pathname) =>
  MARKETING_PATHS.includes(pathname)
  || MARKETING_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));

// Which audience a surface is pitched at. Drives the nav CTA, so the button
// always names the next step that audience actually takes:
//   students  self-serve into the practice flow
//   teachers  self-serve into a class of their own
//   schools   talk to us first - a district buy is a conversation, not a signup
// /start is the student conversion flow, so it keeps the student CTA (this
// preserves the old isStudentSurface behaviour).
export const marketingAudience = (pathname) => {
  // The sign-in page is not a pitch: whoever lands there without an account
  // needs one, not a demo (Sandy 2026-10-06).
  if (pathname === '/signin') return 'signin';
  // The root is the teacher pitch (Sandy 2026-11-03).
  if (pathname === '/students' || pathname === '/testimonials' || pathname.startsWith('/start')) return 'student';
  if (pathname === '/' || pathname === '/teachers') return 'teacher';
  return 'school';
};

// Where the nav's Schools / Teachers / Students links (desktop bar and
// phone menu) go.
export const AUDIENCE_LINKS = {
  schools: '/for-schools',
  teachers: '/teachers',
  students: '/students',
};

// "/" lights up the same nav link as "/teachers" (the same page).
export const activeMarketingPath = (pathname) => (pathname === '/' ? '/teachers' : pathname);

// Sign in / sign up live BEHIND these CTAs rather than as bare nav links, so
// the header offers one obvious action per audience instead of three.
export const MARKETING_CTA = {
  student: { label: 'Create an account', to: '/signup' },
  // The teacher start survey, which ends in the new teacher signup
  // (2026-10-28; it replaced the old /teacherprogram/signup).
  teacher: { label: 'Set up my class', to: '/teachers/start' },
  school: { label: 'Book a demo', to: '/contact?mode=school' },
  signin: { label: 'Create an account', to: '/signup' },
};

// Every "Set up my class" goes through the start survey (2026-09-30).
export const TEACHER_SURVEY_PATH = '/teachers/start';
export const ctaTarget = (pathname, to) => (to === '/teacherprogram/signup' ? TEACHER_SURVEY_PATH : to);

// The schools landing (Formative pattern, 2026-09-30): on THIS path only,
// every nav CTA (desktop bar, phone bar, phone menu) reads "Get in touch" and
// jumps to the inline form (#get-in-touch) on the same page. Every other page
// returns null and keeps its CTA untouched.
export const SCHOOLS_PATH = '/for-schools';
export const ctaJump = (pathname) => (pathname === SCHOOLS_PATH ? { label: 'Get in touch', hash: 'get-in-touch' } : null);
